/**
 * This is the entry point for Feature Hub App integration
 */

import React from 'react';
import type {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
} from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import { FootnoteContextProvider } from '@oneaudi/feature-app-utils';
import I18nContextComponent from '@oneaudi/i18n-context';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';

import type { Logger } from '@feature-hub/logger';
import type { GfaLocaleServiceV1 } from '@volkswagen-onehub/gfa-locale-service';
import type { ContentServiceV1 } from '@oneaudi/content-service';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import type { AudiFootnoteRefernceServiceScopeManagerInterfaceV3 } from '@oneaudi/footnote-reference-service';
import type { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import type { VueFormatterServiceInterfaceV1 } from '@oneaudi/vue-formatter-service';
import type { I18NServiceV1 } from '@oneaudi/i18n-service';
import type { RenderModeServiceV1 } from '@oneaudi/render-mode-service';

import { getDesignLibrary } from '../alpha-helper';
import App from './FeatureApp';
import ContextProvider from './Context';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'gfa:locale-service': GfaLocaleServiceV1;
  readonly 'audi-content-service': ContentServiceV1;
  readonly 'audi-tracking-service'?: TrackingServiceV2;
  readonly 'audi-footnote-reference-service': AudiFootnoteRefernceServiceScopeManagerInterfaceV3;
  readonly 'locale-service': LocaleServiceV1;
  readonly 'dbad:audi-vue-formatter-service': VueFormatterServiceInterfaceV1;
  readonly 'dbad:audi-i18n-service': I18NServiceV1;
  readonly 'audi-render-mode-service'?: RenderModeServiceV1;
}

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      'gfa:locale-service': '^1.0.0',
      'audi-content-service': '^1.0.0',
      'locale-service': '^1.0.0',
      'dbad:audi-i18n-service': '^1.0.0',
    },
    externals: {
      react: '*',
      'styled-components': '*',
      ...getDesignLibrary({ unifiedWebCommonVersion: '*', unifiedWebAlphaCommonVersion: '*' }),
    },
  },
  optionalDependencies: {
    featureServices: {
      'audi-tracking-service': '^2.0.0',
      'audi-footnote-reference-service': '^3.0.0',
      'dbad:audi-vue-formatter-service': '^1.0.0',
      'audi-render-mode-service': '^1.0.0',
    },
  },

  create: ({
    featureServices,
    featureAppId,
    featureAppName,
  }: FeatureAppEnvironment<FeatureServiceDependencies, void>) => {
    const loggerService = featureServices['s2:logger'];
    loggerService?.info('Feature App created.');

    const contentService = featureServices['audi-content-service'];

    const trackingService = featureServices['audi-tracking-service'];
    if (trackingService) {
      trackingService.featureAppName = __FEATURE_APP_NAME__;
    }

    const footnoteReferenceServiceScopeManager = featureServices['audi-footnote-reference-service'];
    const footnoteReferenceService =
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      footnoteReferenceServiceScopeManager?.getDefaultScopeRefService();

    const localeService = featureServices['locale-service'];
    const vueFormatterService = featureServices['dbad:audi-vue-formatter-service'];

    const i18nData = featureServices['dbad:audi-i18n-service'];

    const renderModeService = featureServices['audi-render-mode-service'];

    return {
      render: () => {
        return (
          <UniversalEditorProvider
            contentService={featureServices['audi-content-service']}
            renderModeService={renderModeService}
          >
            <I18nContextComponent
              i18nData={i18nData}
              scopes={['nemo.common', 'fa.article-teaser']}
              featureServices={featureServices}
            >
              <FootnoteContextProvider footnoteReferenceService={footnoteReferenceService}>
                <ContextProvider
                  featureAppEnv={{ featureAppId, featureAppName }}
                  loggerService={loggerService}
                  trackingService={trackingService}
                  localeService={localeService}
                  vueFormatterService={vueFormatterService}
                  renderModeService={renderModeService}
                >
                  <div data-fefa-custom-id={footnoteReferenceService.getConsumerId()}>
                    <App contentService={contentService} />
                  </div>
                </ContextProvider>
              </FootnoteContextProvider>
            </I18nContextComponent>
          </UniversalEditorProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
